// Confirm dialog for business-logic warnings: the save / action was blocked
// only by ignorable (type 'W') warnings — list them and let the user either
// fix the data (Cancel) or acknowledge and proceed (the caller retries the
// same call with the warning codes in `ignore_warnings`). Overlay pattern
// mirrors SelectGridModal / HtmlResultModal, but rendered through a PORTAL to
// document.body: the confirm is opened from inside panels/editors that form
// their own stacking contexts (the live-preview record editor sits at
// z-index 9430, other designer modals up to 11000), so an inline overlay
// would be trapped behind them.

import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import type { WarningItem } from './contracts'
import { tw } from '../testHooks'

interface Props {
  title?: string
  warnings: WarningItem[]
  onCancel: () => void
  onProceed: () => void
  /** Label of the proceed button. Default "Ignore warning(s) and proceed";
   *  imported Vision apps use "OK" (Vision's own OK / Cancel warning box). */
  proceedLabel?: string
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  // Above every host surface (designer editors 9430-10001, run bar 11000) —
  // a blocking confirm must never hide behind the panel that opened it.
  zIndex: 12000,
}

export default function WarningsConfirmModal({ title, warnings, onCancel, onProceed, proceedLabel }: Props) {
  if (typeof document === 'undefined') return null // SSR safety
  return createPortal(
    <div style={overlayStyle} onClick={onCancel}>
      <div
        onClick={e => e.stopPropagation()}
        {...tw('msg', 'warning', { dialog: 'warnings' })}
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(560px, 94vw)', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.9625rem' }}>
            {title || `Warning${warnings.length === 1 ? '' : 's'}`}
          </span>
          <button
            onClick={onCancel}
            {...tw('action', 'close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1625rem', color: 'var(--color-text-secondary, #666)' }}
            title="Close">×</button>
        </div>

        <div style={{ overflow: 'auto', flex: 1, padding: '12px 16px' }}>
          <div style={{ fontSize: '0.8825rem', color: 'var(--color-text-secondary, #555)', marginBottom: 10 }}>
            {proceedLabel
              ? `Press ${proceedLabel} to continue, or Cancel to go back and change the data.`
              : <>Review the warning{warnings.length === 1 ? '' : 's'} below. You can go back and
                fix the data, or ignore {warnings.length === 1 ? 'it' : 'them'} and proceed.</>}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {warnings.map((w, i) => (
              <li key={i} style={{ fontSize: '0.9025rem', lineHeight: 1.4 }}>
                <span style={{ color: 'var(--color-warning, #b45309)' }}>⚠</span>{' '}
                {w.field ? <strong>{w.field}: </strong> : null}
                {w.message}
                {w.code ? (
                  <span style={{ marginLeft: 6, fontSize: '0.8125rem', color: 'var(--color-text-secondary, #888)' }}>
                    ({w.code})
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px', borderTop: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <button
            onClick={onCancel}
            {...tw('action', 'cancel')}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: '1px solid var(--color-border, #ccc)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text, #111)' }}>
            Cancel
          </button>
          <button
            onClick={onProceed}
            {...tw('action', 'confirm')}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: 'none', background: 'var(--color-warning, #d97706)', color: '#fff', cursor: 'pointer' }}>
            {proceedLabel || `Ignore warning${warnings.length === 1 ? '' : 's'} and proceed`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface PendingConfirm {
  warnings: WarningItem[]
  resolve: (proceed: boolean) => void
}

/**
 * Stateful wrapper for the confirm flow: `confirmWarnings` resolves when the
 * user picks Cancel (false) or Proceed (true); render `warningsModal`
 * somewhere in the host component's tree. Pairs with
 * `attemptWithWarningConfirm` from ./warnings:
 *
 *   const { confirmWarnings, warningsModal } = useWarningConfirm()
 *   await attemptWithWarningConfirm(
 *     (ignore) => save({ ...body, ignore_warnings: ignore }),
 *     confirmWarnings,
 *   )
 *   …
 *   {warningsModal}
 */
export function useWarningConfirm(proceedLabel?: string): {
  confirmWarnings: (warnings: WarningItem[]) => Promise<boolean>
  warningsModal: React.ReactNode
} {
  const [pending, setPending] = useState<PendingConfirm | null>(null)

  const confirmWarnings = useCallback((warnings: WarningItem[]) => {
    return new Promise<boolean>((resolve) => {
      setPending({ warnings, resolve })
    })
  }, [])

  const settle = (proceed: boolean) => {
    setPending((p) => {
      p?.resolve(proceed)
      return null
    })
  }

  const warningsModal = pending ? (
    <WarningsConfirmModal
      warnings={pending.warnings}
      onCancel={() => settle(false)}
      onProceed={() => settle(true)}
      proceedLabel={proceedLabel}
    />
  ) : null

  return { confirmWarnings, warningsModal }
}
